/**
 * V4Sentinel — flat text Start / End markers.
 *
 * User correction during Phase 7g: "MANUAL START AND END NODE
 * (SUPPOSED TO BE JUST FLAT TEXT SHAPES)". V2Sentinel renders both as
 * 72×72 colored circles that read like full nodes — V4 wants them as
 * understated boundary markers so the actual work nodes carry the
 * visual weight.
 *
 * Design:
 *   • A subtle pill — small color dot + uppercase label.
 *   • Start = emerald dot, End = slate dot.
 *   • No drop shadow, no thick border — just enough chrome to read as
 *     a marker, not a node.
 *   • Compact (~28px tall) so the spine reads as a sequence of work
 *     nodes punctuated by quiet boundary markers.
 *
 * Compatibility with V2 layout / IR pipeline:
 *   • Same data shape as V2Sentinel — `data.kind` is 'start' or 'end'.
 *   • Same React Flow handle positions (target Top, source Bottom)
 *     so existing edges connect cleanly without re-routing.
 *   • execStatus ring preserved for parity with V2 (the Run path
 *     decorates sentinels with the run's pending/running tint).
 *
 * Scoped to V4 — V2 / V3 keep the original V2Sentinel via
 * V2_NODE_TYPES. V4CanvasView swaps in V4Sentinel through its own
 * nodeTypes override.
 */

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { ACCENT, S, FONT, WEIGHT, RADIUS, SPACING } from './v4Tokens';

const HANDLE_STYLE = {
  width: 6, height: 6,
  background: '#94a3b8',
  border: '1px solid #ffffff',
};

const TONE = {
  start: { dot: ACCENT.emerald.icon, fg: ACCENT.emerald.text, bg: ACCENT.emerald.bg },
  end:   { dot: S[600],              fg: S[700],              bg: S[100] },
};

// Light tint for the in-progress execution ring (mirrors V2's behaviour
// without the heavy box-shadow stack). Returns null when no exec status.
function execRingFor(execStatus) {
  if (!execStatus || execStatus === 'pending') return null;
  if (execStatus === 'running')   return { outline: `2px solid ${ACCENT.sky.icon}`, outlineOffset: 1 };
  if (execStatus === 'completed') return { outline: `2px solid ${ACCENT.emerald.icon}`, outlineOffset: 1 };
  if (execStatus === 'failed')    return { outline: `2px solid ${ACCENT.rose.icon}`, outlineOffset: 1 };
  return null;
}

export default function V4Sentinel({ data }) {
  const kind = (data && data.kind === 'end') ? 'end' : 'start';
  const tone = TONE[kind];
  const label = kind === 'start' ? 'Start' : 'End';
  const ring = execRingFor(data?.execStatus);

  // Earlier draft (2026-05-29 morning) rendered as `display: inline-flex`
  // and let ReactFlow's outer wrapper extend beyond the visible pill.
  // ELK allocates a 53×53 footprint per sentinel (V2_DIMS.SENTINEL_W/H);
  // the small pill sat centered inside it, so edges anchored at the
  // wrapper's top/bottom touched empty space ~15px above/below the
  // visible pill — operator reported "looks disconnected, partially
  // painted". Fix: wrapper now fills the full ELK box (width/height
  // 100%) so the visible painted area === the handle anchor area.
  // Pill content (color dot + uppercase label) is centered inside.
  return (
    <div
      aria-label={label}
      style={{
        position: 'relative',
        width:  '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: SPACING.s,
        padding: `0 ${SPACING.s}px`,
        background: tone.bg,
        color: tone.fg,
        border: `0.5px solid ${S[200]}`,
        borderRadius: RADIUS.pill,
        fontSize: FONT.sm,
        fontWeight: WEIGHT.bold,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        boxSizing: 'border-box',
        ...(ring || {}),
      }}
    >
      <Handle type="target" position={Position.Top} style={HANDLE_STYLE} />
      <span
        style={{
          width: 6, height: 6,
          borderRadius: '50%',
          background: tone.dot,
          display: 'inline-block',
          flexShrink: 0,
        }}
        aria-hidden="true"
      />
      {label}
      <Handle type="source" position={Position.Bottom} style={HANDLE_STYLE} />
    </div>
  );
}
